export interface AnnotatedTreatmentMarker {
  planned_treatment?: Record<string, unknown>;
}

export interface TreatmentSummaryItem {
  key: string;
  product: string;
  dose: number;
  unit: string;
  markerCount: number;
}

function textValue(value: unknown): string {
  return typeof value === "string"
    ? value.trim()
    : "";
}

function numericValue(value: unknown): number | null {
  if (
    typeof value === "number" &&
    Number.isFinite(value)
  ) {
    return value;
  }

  if (
    typeof value === "string" &&
    value.trim()
  ) {
    const parsed = Number(
      value.replace(",", ".")
    );

    return Number.isFinite(parsed)
      ? parsed
      : null;
  }

  return null;
}

export function formatTreatmentNumber(
  value: number
): string {
  return new Intl.NumberFormat("es-AR", {
    maximumFractionDigits: 3,
  }).format(value);
}

export function markerTreatmentAnnotation(
  marker: AnnotatedTreatmentMarker
): string | null {
  const treatment =
    marker.planned_treatment || {};
  const dose = numericValue(
    treatment.dose
  );
  const unit = textValue(
    treatment.unit
  );
  const product = textValue(
    treatment.product
  );

  if (dose !== null) {
    return [
      formatTreatmentNumber(dose),
      unit,
    ].filter(Boolean).join(" ");
  }

  return product || null;
}

export function markerTreatmentProduct(
  marker: AnnotatedTreatmentMarker
): string {
  const treatment =
    marker.planned_treatment || {};

  return (
    textValue(treatment.product) ||
    textValue(treatment.treatment) ||
    "Tratamiento"
  );
}

export function summarizeMarkerTreatments(
  markers: AnnotatedTreatmentMarker[]
): TreatmentSummaryItem[] {
  const totals = new Map<
    string,
    TreatmentSummaryItem
  >();

  for (const marker of markers) {
    const treatment =
      marker.planned_treatment || {};
    const dose = numericValue(
      treatment.dose
    );

    if (dose === null) continue;

    const product =
      markerTreatmentProduct(marker);
    const unit =
      textValue(treatment.unit) ||
      "sin unidad";
    const key =
      product.toLocaleLowerCase("es-AR") +
      "::" +
      unit.toLocaleLowerCase("es-AR");
    const current = totals.get(key);

    if (current) {
      current.dose += dose;
      current.markerCount += 1;
      continue;
    }

    totals.set(key, {
      key,
      product,
      dose,
      unit,
      markerCount: 1,
    });
  }

  return Array.from(totals.values());
}
